import { useState, useCallback } from 'react';
import { handleApiError, processError, getErrorDisplay } from '../utils/errorHandler';
import { ERROR_CONTEXTS } from '../utils/errorMessages';

/**
 * Custom hook for centralized error handling in onboarding components
 * Follows existing Redux patterns and integrates with MessageBox component
 */
export const useErrorHandler = (defaultContext = ERROR_CONTEXTS.GENERAL) => {
  const [error, setError] = useState(null);
  const [errorDisplay, setErrorDisplay] = useState(null);

  /**
   * Clear current error state
   */
  const clearError = useCallback(() => {
    setError(null);
    setErrorDisplay(null);
  }, []);

  /**
   * Handle error and update state
   * @param {Error} err - The error object
   * @param {string} context - Context of the operation
   */
  const handleError = useCallback((err, context = defaultContext) => {
    const errorInfo = handleApiError(err, context);
    const displayInfo = getErrorDisplay(errorInfo);
    
    setError(errorInfo);
    setErrorDisplay(displayInfo);
    
    return errorInfo;
  }, [defaultContext]);

  /**
   * Process error silently (same as handleError since no toast)
   * @param {Error} err - The error object
   * @param {string} context - Context of the operation
   */
  const processErrorSilent = useCallback((err, context = defaultContext) => {
    const errorInfo = processError(err, context);
    const displayInfo = getErrorDisplay(errorInfo);
    
    setError(errorInfo);
    setErrorDisplay(displayInfo);
    
    return errorInfo;
  }, [defaultContext]);

  /**
   * Set custom error message (for validation errors, etc.)
   * @param {string} message - Error message to display
   * @param {string} variant - MessageBox variant ('error', 'warning', 'info')
   */
  const setCustomError = useCallback((message, variant = 'error') => {
    const customError = {
      type: 'custom',
      message,
      context: defaultContext
    };
    
    const displayInfo = {
      message,
      variant,
      type: 'custom'
    };
    
    setError(customError);
    setErrorDisplay(displayInfo);
  }, [defaultContext]);

  /**
   * Handle multiple errors (for form validation)
   * @param {Object} errors - Object with field names as keys and error messages as values
   */
  const setValidationErrors = useCallback((errors) => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) {
      clearError();
      return;
    }

    // Take the first error message for display
    const firstError = errors[errorKeys[0]];
    setCustomError(firstError, 'warning');
  }, [setCustomError, clearError]);

  /**
   * Get props for MessageBox component
   * @returns {Object|null} Props for MessageBox or null if no error
   */
  const getMessageBoxProps = useCallback(() => {
    if (!errorDisplay) return null;
    
    return {
      variant: errorDisplay.variant,
      children: errorDisplay.message
    };
  }, [errorDisplay]);

  /**
   * Check if there's a specific type of error
   * @param {string} errorType - Type to check for
   * @returns {boolean}
   */
  const hasErrorType = useCallback((errorType) => {
    return error?.type === errorType;
  }, [error]);

  /**
   * Get the current error message
   * @returns {string|null}
   */
  const getErrorMessage = useCallback(() => {
    return errorDisplay?.message || null;
  }, [errorDisplay]);

  return {
    // Error state
    error,
    errorDisplay,
    hasError: !!error,
    errorMessage: getErrorMessage(),
    
    // Error handling methods
    handleError,
    processErrorSilent,
    setCustomError,
    setValidationErrors,
    clearError,
    
    // Helper methods
    getMessageBoxProps,
    hasErrorType
  };
};

/**
 * Hook for form validation error handling
 * Specifically designed for onboarding forms
 */
export const useFormErrorHandler = (context = ERROR_CONTEXTS.GENERAL) => {
  const [fieldErrors, setFieldErrors] = useState({});
  const [pageError, setPageErrorState] = useState(null);
  
  const errorHandler = useErrorHandler(context);

  /**
   * Set error for a specific field
   * @param {string} fieldName - Name of the field
   * @param {string} errorMessage - Error message
   */
  const setFieldError = useCallback((fieldName, errorMessage) => {
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: errorMessage
    }));
  }, []);

  /**
   * Clear error for a specific field
   * @param {string} fieldName - Name of the field
   */
  const clearFieldError = useCallback((fieldName) => {
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  /**
   * Clear all field errors
   */
  const clearFieldErrors = useCallback(() => {
    setFieldErrors({});
  }, []);

  /**
   * Set page-level error (for API errors)
   * @param {Error|string} error - Error object or message
   * @param {string} context - Context of the operation
   */
  const setPageError = useCallback((error, context) => {
    if (typeof error === 'string') {
      errorHandler.setCustomError(error);
      setPageErrorState(error);
    } else {
      const errorInfo = errorHandler.handleError(error, context);
      setPageErrorState(errorInfo.message);
    }
  }, [errorHandler]);

  /**
   * Clear page-level error
   */
  const clearPageError = useCallback(() => {
    setPageErrorState(null);
    errorHandler.clearError();
  }, [errorHandler]);

  /**
   * Clear all errors (field and page)
   */
  const clearAllErrors = useCallback(() => {
    clearFieldErrors();
    clearPageError();
  }, [clearFieldErrors, clearPageError]);

  /**
   * Get field error message
   * @param {string} fieldName - Name of the field
   * @returns {string|null}
   */
  const getFieldError = useCallback((fieldName) => {
    return fieldErrors[fieldName] || null;
  }, [fieldErrors]);

  /**
   * Check if field has error
   * @param {string} fieldName - Name of the field
   * @returns {boolean}
   */
  const hasFieldError = useCallback((fieldName) => {
    return !!fieldErrors[fieldName];
  }, [fieldErrors]);

  return {
    // Field errors
    fieldErrors,
    setFieldError,
    clearFieldError,
    clearFieldErrors,
    getFieldError,
    hasFieldError,
    
    // Page errors
    pageError,
    setPageError,
    clearPageError,
    hasPageError: !!pageError,
    
    // Combined
    clearAllErrors,
    hasAnyError: Object.keys(fieldErrors).length > 0 || !!pageError,
    
    // MessageBox props for page error
    getPageErrorProps: () => pageError ? { variant: 'error', children: pageError } : null
  };
};